import { useCallback, useEffect, useState } from "react";
import { ApiError, getApi } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import { API_OFFBOARDING_MY_REQUEST } from "../../../../../config/Endpoints.js";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";
import { SQE_OFFBOARDING_URL, parseSqeOffboarding, unwrapSqeOffboardingResponse } from "../api/sqe-offboarding-api.ts";
import {
    OffboardingRequest,
    OffboardingViewState,
    resolveOffboardingViewState,
} from "../schemas/index.ts";

type OffboardingResponse = {
    offboarding: OffboardingRequest | null;
    pip: unknown;
    isOnPip: boolean;
};

export function useOffboardingRequest() {
    const [request, setRequest] = useState<OffboardingRequest | null>(null);
    const [pip, setPip] = useState<unknown>(null);
    const [isOnPip, setIsOnPip] = useState(false);
    const [viewState, setViewState] = useState<OffboardingViewState>("INACTIVE");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isSqe = useSqeLabs();

    const fetchRequest = useCallback(async (signal?: AbortSignal) => {
        setLoading(true);
        setError(null);

        try {
            const { data } = await getApi<OffboardingResponse>(
                isSqe ? `${SQE_OFFBOARDING_URL}/my-request` : API_OFFBOARDING_MY_REQUEST,
                accessToken(),
                "json",
                signal,
            );

            const response = isSqe ? unwrapSqeOffboardingResponse(data) as OffboardingResponse | null : data;
            if (response == null) {
                setRequest(null);
                setPip(null);
                setIsOnPip(false); 
                setViewState("INACTIVE");
            } else {
                setPip(response.pip ?? null);
                setIsOnPip(response.isOnPip === true);
                const offboarding = isSqe && response.offboarding != null
                    ? parseSqeOffboarding(response.offboarding)
                    : response.offboarding;
                if (offboarding == null) {
                    setRequest(null);
                    setViewState("INACTIVE");
                } else {
                    setRequest(offboarding);
                    setViewState(resolveOffboardingViewState(offboarding));
                }
            }
        } catch (e) {
            if (e instanceof ApiError && e.status === 404) {
                setRequest(null);
                setPip(null);
                setIsOnPip(false);
                setViewState("INACTIVE");
            } else {
                const msg =
                    e instanceof ApiError
                        ? e.message
                        : "Failed to load offboarding request.";
                setError(msg);
            }
        } finally {
            setLoading(false);
        }
    }, [isSqe]);




    useEffect(() => {
        const ac = new AbortController();
        void fetchRequest(ac.signal);
        return () => ac.abort();
    }, [fetchRequest]);

    return {
        request,
        pip,
        isOnPip,
        viewState,
        offboardingId: request?.id ?? null,
        loading,
        error,
        refetch: fetchRequest,
    };
}
